import { PRESETS, lerpPreset } from '../renderer/postfx/gradePresets.mjs';

const LS_KEY = 'hexaworlds_timeofday';

const DEFAULTS = {
  enabled: true,
  cycleMinutes: 8,
  strength: 0.6,
};

/**
 * WorldClock drives a slow day/night cycle.
 * Phase 0..1 maps to:  0=midnight, 0.25=dawn, 0.5=noon, 0.75=sunset, 1=midnight
 */
export class WorldClock {
  constructor() {
    this.startMs = performance.now();
    this.timeScale = 1.0;
    this.cycleMs = DEFAULTS.cycleMinutes * 60 * 1000;
    this.enabled = DEFAULTS.enabled;
    this.strength = DEFAULTS.strength;
    this._loadSettings();
  }

  /** Phase 0..1 through the day cycle. */
  getPhase01(nowMs) {
    const elapsed = (nowMs - this.startMs) * this.timeScale;
    return (elapsed % this.cycleMs) / this.cycleMs;
  }

  /** Sun height 0..1 using cosine curve, peaks at noon (phase=0.5). */
  getSunHeight01(phase01) {
    // cos curve: phase 0 (midnight) → -1, phase 0.5 (noon) → +1
    // remap to 0..1
    return 0.5 + 0.5 * Math.cos(2 * Math.PI * (phase01 - 0.5));
  }

  /**
   * Return an interpolated GradePreset for the current phase.
   * Zones: midnight→dawn→noon→sunset→midnight
   *  0.00 - 0.20  night
   *  0.20 - 0.35  night→dawn
   *  0.35 - 0.45  dawn→noon
   *  0.45 - 0.55  noon
   *  0.55 - 0.65  noon→sunset
   *  0.65 - 0.80  sunset→night
   *  0.80 - 1.00  night
   */
  getPreset(phase01) {
    const p = phase01;
    if (p < 0.20) return PRESETS.night;
    if (p < 0.35) return lerpPreset(PRESETS.night, PRESETS.dawn, (p - 0.20) / 0.15);
    if (p < 0.45) return lerpPreset(PRESETS.dawn, PRESETS.noon, (p - 0.35) / 0.10);
    if (p < 0.55) return PRESETS.noon;
    if (p < 0.65) return lerpPreset(PRESETS.noon, PRESETS.sunset, (p - 0.55) / 0.10);
    if (p < 0.80) return lerpPreset(PRESETS.sunset, PRESETS.night, (p - 0.65) / 0.15);
    return PRESETS.night;
  }

  // ---- Settings persistence ----

  _loadSettings() {
    try {
      const raw = localStorage.getItem(LS_KEY);
      if (raw) {
        const s = JSON.parse(raw);
        if (s.enabled !== undefined) this.enabled = s.enabled;
        if (s.cycleMinutes > 0) {
          this.cycleMs = s.cycleMinutes * 60 * 1000;
        }
        if (s.strength >= 0 && s.strength <= 1) {
          this.strength = s.strength;
        }
        if (s.startMs > 0) this.startMs = s.startMs;
      }
    } catch (_) { /* ignore */ }
  }

  _saveSettings() {
    try {
      localStorage.setItem(LS_KEY, JSON.stringify({
        enabled: this.enabled,
        cycleMinutes: this.cycleMs / 60000,
        strength: this.strength,
        startMs: this.startMs,
      }));
    } catch (_) { /* quota */ }
  }

  setEnabled(v) {
    this.enabled = v;
    this._saveSettings();
  }

  setCycleMinutes(m) {
    this.cycleMs = Math.max(1, m) * 60 * 1000;
    this._saveSettings();
  }

  setStrength(s) {
    this.strength = Math.max(0, Math.min(1, s));
    this._saveSettings();
  }
}
